import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch } from "redux/hooks";
import { setErrors, updateIndividualSigner } from "redux/appSlice";
import { RequestParticipant } from "models/interfaces";
import { isTruthy } from "helpers/methods";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";

interface MobileDelayDaysSheetProps {
  open: boolean;
  onClose: () => void;
  signerToDelay: Partial<RequestParticipant> | Partial<RequestParticipant>[];
}

const DAY_OPTIONS = Array.from({ length: 100 }, (_, i) => i); // 0..99
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i); // 0..23

const MobileDelayDaysSheet: React.FC<MobileDelayDaysSheetProps> = ({
  open,
  onClose,
  signerToDelay,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();

  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [daysAnchor, setDaysAnchor] = useState<null | HTMLElement>(null);
  const [hoursAnchor, setHoursAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const signer = Array.isArray(signerToDelay) ? signerToDelay[0] : signerToDelay;
    setDays(Number(signer?.delayDays) || 0);
    setHours(Number(signer?.delayHours) || 0);
  }, [open, signerToDelay]);

  const createDelay = () => {
    const fields = {
      delayDays: String(days),
      delayHours: String(hours),
      scheduleTime: undefined,
    };
    const targets = Array.isArray(signerToDelay)
      ? signerToDelay
      : [signerToDelay];
    targets.forEach((s) => {
      if (isTruthy(s?.id) || s?.id === 0) {
        dispatch(
          updateIndividualSigner({ signerId: s.id, fieldsToUpdate: fields }),
        );
      }
    });
    dispatch(setErrors({ delay: "" }));
    onClose();
  };

  const renderNumberMenu = (
    anchor: null | HTMLElement,
    onCloseMenu: () => void,
    options: number[],
    onPick: (n: number) => void,
  ) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onCloseMenu}
      PaperProps={{ sx: { maxHeight: 260, width: 140 } }}
      MenuListProps={{ "aria-label": "Select a number" }}
    >
      {options.map((n) => (
        <MenuItem
          key={n}
          onClick={() => {
            onPick(n);
            onCloseMenu();
          }}
        >
          <Typography variant="subtitle2">{n}</Typography>
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "delay-days-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          id="delay-days-sheet-title"
          component="h2"
          sx={{ fontSize: "1rem", fontWeight: 600, color: m.base[900] }}
        >
          Delay by days
        </Typography>
        <Typography
          {...pressableProps(() => {
            setDays(0);
            setHours(0);
          }, { label: "Clear" })}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: m.grey[400],
            cursor: "pointer",
          }}
        >
          Clear
        </Typography>
      </Box>

      {/* Days + Hours */}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...classes.fieldLabel, mt: 0 }}>Days</Typography>
          <Box
            sx={classes.dropdownField}
            {...pressableProps((e) => setDaysAnchor(e.currentTarget as HTMLElement), {
              label: `Days, ${days} selected`,
              hasPopup: "menu",
              expanded: Boolean(daysAnchor),
            })}
          >
            <Typography sx={classes.dropdownFieldText(false)}>{days}</Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: m.grey[400] }} />
          </Box>
          {renderNumberMenu(
            daysAnchor,
            () => setDaysAnchor(null),
            DAY_OPTIONS,
            setDays,
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...classes.fieldLabel, mt: 0 }}>Hours</Typography>
          <Box
            sx={classes.dropdownField}
            {...pressableProps((e) => setHoursAnchor(e.currentTarget as HTMLElement), {
              label: `Hours, ${hours} selected`,
              hasPopup: "menu",
              expanded: Boolean(hoursAnchor),
            })}
          >
            <Typography sx={classes.dropdownFieldText(false)}>
              {hours}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: m.grey[400] }} />
          </Box>
          {renderNumberMenu(
            hoursAnchor,
            () => setHoursAnchor(null),
            HOUR_OPTIONS,
            setHours,
          )}
        </Box>
      </Box>

      {/* Selected summary */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: m.ePurple[50],
          borderRadius: "14px",
          px: 2,
          py: 1.75,
          mt: 2.5,
        }}
      >
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: m.base[500] }}
        >
          Selected
        </Typography>
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 700, color: m.ePurple[700] }}
        >
          {days} Days {hours} hours
        </Typography>
      </Box>

      {/* Create delay */}
      <Button
        fullWidth
        variant="contained"
        disableElevation
        disabled={days === 0 && hours === 0}
        onClick={createDelay}
        sx={{ ...classes.continueButton, width: "100%", mt: 2, py: 1.75 }}
      >
        Create delay
      </Button>
    </Drawer>
  );
};

export default MobileDelayDaysSheet;
